import React, { useState, useRef } from 'react';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { collection, addDoc, writeBatch, doc, getDocs, query, where } from '../firebase.config';
import { db } from '../firebase.config';
import * as XLSX from 'xlsx';
import { useNotification } from '../contexts/NotificationContext';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from './shared/AppSelect';

const SimpleImport = () => {
  const { currentCompany } = useEnhancedCompany();
  const companyId = currentCompany?.companyId || currentCompany?.id;
  const { success, error, warning, info } = useNotification();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileData, setFileData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [fieldMappings, setFieldMappings] = useState({});
  const [mappedData, setMappedData] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [duplicateItems, setDuplicateItems] = useState([]);
  const [existingItems, setExistingItems] = useState([]);
  
  const fileInputRef = useRef(null);

  // All possible item fields for mapping
  const itemFields = [
    { key: 'itemName', label: 'Item Name', required: true, description: 'Name of the product/item' },
    { key: 'description', label: 'Description', required: false, description: 'Detailed description of the item' },
    { key: 'defaultRate', label: 'Default Rate/Price', required: false, description: 'Selling price of the item' },
    { key: 'quantityMeasurement', label: 'Unit/Measurement', required: false, description: 'Unit of measurement (pcs, kg, liter, etc.)' },
    { key: 'barcodeNumber', label: 'Barcode Number', required: false, description: 'Barcode or SKU number' },
    { key: 'hsnCode', label: 'HSN Code', required: false, description: 'HSN code for GST purposes' },
    { key: 'gstRate', label: 'GST Rate (%)', required: false, description: 'GST percentage rate' },
    { key: 'category', label: 'Category', required: false, description: 'Item category or type' },
    { key: 'openingStock', label: 'Opening Stock', required: false, description: 'Initial stock quantity' },
    { key: 'minStockLevel', label: 'Minimum Stock Level', required: false, description: 'Minimum stock alert level' },
    { key: 'maxStockLevel', label: 'Maximum Stock Level', required: false, description: 'Maximum stock level' },
    { key: 'itemType', label: 'Item Type', required: false, description: 'Type of item (product, service, etc.)' }
  ];

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setFileData([]);
    setHeaders([]);
    setSelectedRows(new Set());
    setFieldMappings({});
    setMappedData([]);
    setImportResult(null);

    try {
      info('Reading file...', { title: 'Loading File', duration: 3000 });
      
      const data = await readExcelFile(file);
      
      if (!data || data.length === 0) {
        error('No data found in file', { title: 'No Data', duration: 7000 });
        return;
      }

      setFileData(data);
      const headers = Object.keys(data[0]);
      setHeaders(headers);
      
      // Auto-select all rows by default
      setSelectedRows(new Set(data.map((_, index) => index)));
      
      // Auto-map common fields
      const autoMappings = {};
      headers.forEach(header => {
        const lowerHeader = header.toLowerCase();
        itemFields.forEach(field => {
          if (field.key === 'itemName' && (lowerHeader.includes('item') || lowerHeader.includes('name') || lowerHeader.includes('product'))) {
            autoMappings[field.key] = header;
          } else if (field.key === 'defaultRate' && (lowerHeader.includes('rate') || lowerHeader.includes('price') || lowerHeader.includes('amount'))) {
            autoMappings[field.key] = header;
          } else if (field.key === 'quantityMeasurement' && (lowerHeader.includes('unit') || lowerHeader.includes('measurement') || lowerHeader.includes('uom'))) {
            autoMappings[field.key] = header;
          } else if (field.key === 'barcodeNumber' && (lowerHeader.includes('barcode') || lowerHeader.includes('sku') || lowerHeader.includes('code'))) {
            autoMappings[field.key] = header;
          } else if (field.key === 'hsnCode' && lowerHeader.includes('hsn')) {
            autoMappings[field.key] = header;
          } else if (field.key === 'gstRate' && (lowerHeader.includes('gst') || lowerHeader.includes('tax'))) {
            autoMappings[field.key] = header;
          } else if (field.key === 'category' && (lowerHeader.includes('category') || lowerHeader.includes('type') || lowerHeader.includes('group'))) {
            autoMappings[field.key] = header;
          } else if (field.key === 'openingStock' && (lowerHeader.includes('stock') || lowerHeader.includes('qty') || lowerHeader.includes('quantity'))) {
            autoMappings[field.key] = header;
          }
        });
      });
      setFieldMappings(autoMappings);

      success(`File loaded successfully! Found ${data.length} records.`, { 
        title: 'File Loaded',
        details: `${data.length} records ready to import`,
        duration: 6000 
      });
      setCurrentStep(2);
    } catch (err) {
      console.error('File read error:', err);
      error(`Failed to read file: ${err.message}`, { title: 'File Read Error', duration: 7000 });
    }
  };

  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Get the first sheet
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          
          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
          
          // Find header row (look for row with most non-empty cells)
          let headerRowIndex = 0;
          let maxNonEmptyCells = 0;
          
          for (let i = 0; i < Math.min(jsonData.length, 10); i++) {
            const row = jsonData[i];
            const nonEmptyCells = row.filter(cell => cell && String(cell).trim() !== '').length;
            if (nonEmptyCells > maxNonEmptyCells) {
              maxNonEmptyCells = nonEmptyCells;
              headerRowIndex = i;
            }
          }
          
          // Extract headers and data
          const headers = jsonData[headerRowIndex] || [];
          const dataRows = jsonData.slice(headerRowIndex + 1).filter(row => 
            row.some(cell => cell && String(cell).trim() !== '')
          );
          
          // Convert to objects
          const result = dataRows.map(row => {
            const obj = {};
            headers.forEach((header, index) => {
              if (header) {
                obj[String(header).trim()] = row[index] || '';
              }
            });
            return obj;
          });
          
          resolve(result);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const handleSelectAll = () => {
    setSelectedRows(new Set(fileData.map((_, index) => index)));
  };

  const handleDeselectAll = () => {
    setSelectedRows(new Set());
  };

  const handleRowToggle = (index) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedRows(newSelected);
  };

  // Fetch existing items for duplicate check
  const fetchExistingItems = async () => {
    if (!companyId) return [];
    
    try {
      const itemsRef = collection(db, 'companies', companyId, 'items');
      const snapshot = await getDocs(itemsRef);
      const items = [];
      snapshot.forEach((doc) => {
        items.push({ id: doc.id, ...doc.data() });
      });
      return items;
    } catch (error) {
      console.error('Error fetching existing items:', error);
      return [];
    }
  };

  const generateMappedPreview = async () => {
    const selectedData = fileData.filter((_, index) => selectedRows.has(index));
    
    console.log('Selected data:', selectedData);
    console.log('Field mappings:', fieldMappings);
    
    // Fetch existing items for duplicate check
    const existing = await fetchExistingItems();
    setExistingItems(existing);
    
    const mapped = selectedData.map((row, index) => {
      const item = {
        id: `temp_${index}`,
        itemName: '',
        description: '',
        defaultRate: 0,
        quantityMeasurement: 'pcs',
        barcodeNumber: '',
        hsnCode: '',
        gstRate: 0,
        category: '',
        openingStock: 0,
        currentStock: 0,
        minStockLevel: 0,
        maxStockLevel: 0,
        itemType: 'product',
        unit: 'pcs',
        isDuplicate: false,
        duplicateAction: 'skip', // skip, update, merge
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Map fields based on user selection
      Object.entries(fieldMappings).forEach(([fieldKey, columnName]) => {
        if (columnName && row[columnName] !== undefined) {
          const value = row[columnName];
          
          switch (fieldKey) {
            case 'itemName':
              item.itemName = String(value).trim() || `Item ${index + 1}`;
              break;
            case 'description':
              item.description = String(value).trim();
              break;
            case 'defaultRate':
              item.defaultRate = parseFloat(String(value).replace(/[^\d.-]/g, '')) || 0;
              break;
            case 'quantityMeasurement':
              item.quantityMeasurement = String(value).trim() || 'pcs';
              item.unit = String(value).trim() || 'pcs';
              break;
            case 'barcodeNumber':
              item.barcodeNumber = String(value).trim();
              break;
            case 'hsnCode':
              item.hsnCode = String(value).trim();
              break;
            case 'gstRate':
              item.gstRate = parseFloat(String(value).replace(/[^\d.-]/g, '')) || 0;
              break;
            case 'category':
              item.category = String(value).trim();
              break;
            case 'openingStock':
              item.openingStock = parseFloat(String(value).replace(/[^\d.-]/g, '')) || 0;
              item.currentStock = parseFloat(String(value).replace(/[^\d.-]/g, '')) || 0;
              break;
            case 'minStockLevel':
              item.minStockLevel = parseFloat(String(value).replace(/[^\d.-]/g, '')) || 0;
              break;
            case 'maxStockLevel':
              item.maxStockLevel = parseFloat(String(value).replace(/[^\d.-]/g, '')) || 0;
              break;
            case 'itemType':
              item.itemType = String(value).trim() || 'product';
              break;
          }
        }
      });

      // Check for duplicates
      const duplicate = existing.find(existingItem => 
        existingItem.itemName && existingItem.itemName.toLowerCase() === item.itemName.toLowerCase()
      );
      
      if (duplicate) {
        item.isDuplicate = true;
        item.duplicateId = duplicate.id;
        item.existingItem = duplicate;
      }

      return item;
    });

    console.log('Mapped data:', mapped);
    setMappedData(mapped);
    setCurrentStep(3);
  };

  // Handle editing individual items
  const handleEditItem = (index) => {
    setEditingIndex(index);
  };

  const handleSaveEdit = (index, updatedItem) => {
    const newMappedData = [...mappedData];
    newMappedData[index] = { ...updatedItem, id: `temp_${index}` };
    setMappedData(newMappedData);
    setEditingIndex(null);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
  };

  // Handle duplicate action changes
  const handleDuplicateActionChange = (index, action) => {
    const newMappedData = [...mappedData];
    newMappedData[index].duplicateAction = action;
    setMappedData(newMappedData);
  };

  const handleImport = async () => {
    if (!companyId) {
      warning('No company selected', { title: 'Validation Error' });
      return;
    }

    if (!mappedData || mappedData.length === 0) {
      warning('No data to import', { title: 'Validation Error' });
      return;
    }

    setIsImporting(true);
    try {
      info('Importing items...', { title: 'Importing', duration: 3000 });
      
      console.log('Starting import for company:', companyId);
      console.log('Mapped data:', mappedData);
      
      const batch = writeBatch(db);
      const itemsRef = collection(db, 'companies', companyId, 'items');
      
      let successCount = 0;
      let errorCount = 0;

      for (const item of mappedData) {
        try {
          // Validate required fields
          if (!item.itemName || item.itemName.trim() === '') {
            console.warn('Skipping item with empty name:', item);
            errorCount++;
            continue;
          }

          // Handle duplicates
          if (item.isDuplicate && item.duplicateAction === 'skip') {
            console.log('Skipping duplicate item:', item.itemName);
            continue;
          }

          const itemData = {
            itemName: item.itemName.trim(),
            description: item.description || '',
            defaultRate: parseFloat(item.defaultRate) || 0,
            quantityMeasurement: item.quantityMeasurement || 'pcs',
            barcodeNumber: item.barcodeNumber || '',
            hsnCode: item.hsnCode || '',
            gstRate: parseFloat(item.gstRate) || 0,
            category: item.category || '',
            openingStock: parseFloat(item.openingStock) || 0,
            currentStock: parseFloat(item.openingStock) || 0,
            minStockLevel: parseFloat(item.minStockLevel) || 0,
            maxStockLevel: parseFloat(item.maxStockLevel) || 0,
            itemType: item.itemType || 'product',
            unit: item.quantityMeasurement || 'pcs',
            isActive: true,
            companyId: companyId,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };

          if (item.isDuplicate && item.duplicateAction === 'update') {
            // Update existing item
            const docRef = doc(db, 'companies', companyId, 'items', item.duplicateId);
            batch.update(docRef, itemData);
            successCount++;
          } else if (item.isDuplicate && item.duplicateAction === 'merge') {
            // Merge with existing item (combine stock, keep better data)
            const existing = item.existingItem;
            const mergedData = {
              ...itemData,
              openingStock: (parseFloat(existing.openingStock) || 0) + (parseFloat(item.openingStock) || 0),
              currentStock: (parseFloat(existing.currentStock) || 0) + (parseFloat(item.openingStock) || 0),
              description: item.description || existing.description || '',
              barcodeNumber: item.barcodeNumber || existing.barcodeNumber || '',
              hsnCode: item.hsnCode || existing.hsnCode || '',
              category: item.category || existing.category || ''
            };
            const docRef = doc(db, 'companies', companyId, 'items', item.duplicateId);
            batch.update(docRef, mergedData);
            successCount++;
          } else {
            // Create new item
            const docRef = doc(itemsRef);
            batch.set(docRef, itemData);
            successCount++;
          }
        } catch (error) {
          console.error('Error preparing item:', error, item);
          errorCount++;
        }
      }

      console.log(`Preparing to commit batch: ${successCount} items, ${errorCount} errors`);
      await batch.commit();
      
      setImportResult({
        success: successCount,
        errors: errorCount,
        total: mappedData.length
      });
      
      success(`Import completed! ${successCount} items imported successfully.`, { 
        title: 'Import Completed',
        details: `${successCount} successful • ${errorCount} errors • ${mappedData.length} total`,
        duration: 6000 
      });
      setCurrentStep(4);
    } catch (err) {
      console.error('Import error:', err);
      error(`Import failed: ${err.message}`, { title: 'Import Failed', duration: 7000 });
      setImportResult({
        success: 0,
        errors: mappedData.length,
        total: mappedData.length
      });
      setCurrentStep(4);
    } finally {
      setIsImporting(false);
    }
  };

  const resetImport = () => {
    setCurrentStep(1);
    setSelectedFile(null);
    setFileData([]);
    setHeaders([]);
    setSelectedRows(new Set());
    setFieldMappings({});
    setMappedData([]);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Import Items from Excel</h3>
              <p className="text-gray-600 mb-8">Upload your Excel file to import items in bulk</p>
            </div>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
              <div className="text-6xl text-blue-500 mb-4">📊</div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Select Excel File</h4>
              <p className="text-gray-600 mb-6">Choose an Excel file (.xlsx, .xls) containing your items data</p>
              
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer text-lg font-medium"
              >
                Choose Excel File
              </label>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-2xl font-bold text-gray-900">Preview & Select Rows</h3>
              <div className="text-sm text-gray-600">
                {selectedRows.size} of {fileData.length} rows selected
              </div>
            </div>

            {/* Row Selection Controls */}
            <div className="flex space-x-4">
              <button
                onClick={handleSelectAll}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Select All
              </button>
              <button
                onClick={handleDeselectAll}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Deselect All
              </button>
            </div>

            {/* Data Preview Table */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto max-h-96">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-700 w-12">
                        <input
                          type="checkbox"
                          checked={selectedRows.size === fileData.length}
                          onChange={(e) => e.target.checked ? handleSelectAll() : handleDeselectAll()}
                        />
                      </th>
                      {headers.map((header, index) => (
                        <th key={index} className="px-4 py-3 text-left font-medium text-gray-700">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fileData.map((row, index) => (
                      <tr key={index} className={`border-t ${selectedRows.has(index) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedRows.has(index)}
                            onChange={() => handleRowToggle(index)}
                          />
                        </td>
                        {headers.map((header, colIndex) => (
                          <td key={colIndex} className="px-4 py-3 text-gray-600">
                            {String(row[header] || '').substring(0, 50)}
                            {String(row[header] || '').length > 50 && '...'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Field Mapping Section */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Map Excel Columns to Item Fields</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {itemFields.map((field) => (
                  <div key={field.key} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    <AppSelect
                      value={fieldMappings[field.key] || ''}
                      onChange={(e) => setFieldMappings({...fieldMappings, [field.key]: e.target.value})}
                      placeholder="Select Column"
                      aria-label={field.label}
                      sheetTitle={field.label}
                      options={headers.map(header => ({ value: header, label: header }))}
                      triggerClassName={`${APP_SELECT_FILTER_TRIGGER}`}
                    />
                    <p className="text-xs text-gray-500">{field.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={generateMappedPreview}
                disabled={selectedRows.size === 0 || !fieldMappings.itemName}
                className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Generate Preview
              </button>
              <button
                onClick={() => setCurrentStep(1)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-2xl font-bold text-gray-900">Mapped Data Preview</h3>
              <div className="text-sm text-gray-600">
                {mappedData.length} items ready to import
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-blue-800">
                <strong>Preview:</strong> Review the mapped data below. All selected rows will be imported as items.
              </div>
            </div>

            {/* Mapped Data Preview */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto max-h-96">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Status</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Item Name</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Description</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Rate</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Unit</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Barcode</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">HSN</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">GST%</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Category</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Stock</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mappedData.map((item, index) => (
                      <tr key={index} className={`border-t hover:bg-gray-50 ${item.isDuplicate ? 'bg-yellow-50' : ''}`}>
                        <td className="px-4 py-3">
                          {item.isDuplicate ? (
                            <div className="flex flex-col space-y-1">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                Duplicate
                              </span>
                              <AppSelect
                                value={item.duplicateAction}
                                onChange={(e) => handleDuplicateActionChange(index, e.target.value)}
                                aria-label="Duplicate action"
                                sheetTitle="Duplicate action"
                                options={[
                                  { value: 'skip', label: 'Skip' },
                                  { value: 'update', label: 'Update' },
                                  { value: 'merge', label: 'Merge' },
                                ]}
                                triggerClassName="text-xs border border-gray-300 rounded px-1 py-0.5 min-h-[28px] flex items-center justify-between gap-1 touch-manipulation w-full"
                              />
                            </div>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              New
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-900 font-medium">{item.itemName}</td>
                        <td className="px-4 py-3 text-gray-600">{item.description}</td>
                        <td className="px-4 py-3 text-gray-600">₹{item.defaultRate}</td>
                        <td className="px-4 py-3 text-gray-600">{item.quantityMeasurement}</td>
                        <td className="px-4 py-3 text-gray-600">{item.barcodeNumber}</td>
                        <td className="px-4 py-3 text-gray-600">{item.hsnCode}</td>
                        <td className="px-4 py-3 text-gray-600">{item.gstRate}%</td>
                        <td className="px-4 py-3 text-gray-600">{item.category}</td>
                        <td className="px-4 py-3 text-gray-600">{item.openingStock}</td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleEditItem(index)}
                            className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={handleImport}
                disabled={isImporting}
                className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 font-medium disabled:opacity-50"
              >
                {isImporting ? 'Importing...' : `Import ${mappedData.length} Items`}
              </button>
              <button
                onClick={() => setCurrentStep(2)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
            </div>

            {/* Edit Modal */}
            {editingIndex !== null && (
              <EditItemModal
                item={mappedData[editingIndex]}
                onSave={(updatedItem) => handleSaveEdit(editingIndex, updatedItem)}
                onCancel={handleCancelEdit}
              />
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="text-6xl text-green-500 mb-4">✅</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Import Completed Successfully!</h3>
            </div>

            {importResult && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">{importResult.success}</div>
                    <div className="text-gray-600">Items Imported</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-red-600">{importResult.errors}</div>
                    <div className="text-gray-600">Errors</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">{importResult.total}</div>
                    <div className="text-gray-600">Total Processed</div>
                  </div>
                </div>
              </div>
            )}

            <div className="text-center">
              <button
                onClick={resetImport}
                className="bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 font-medium"
              >
                Import More Items
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-8">
          {/* Progress Bar */}
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center justify-between overflow-x-auto">
              {[1, 2, 3, 4].map((step) => (
                <div key={step} className="flex items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                      currentStep >= step
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {currentStep > step ? '✓' : step}
                  </div>
                  {step < 4 && (
                    <div
                      className={`w-20 h-1 mx-2 ${
                        currentStep > step ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="hidden sm:flex justify-between mt-2 text-sm text-gray-600">
              <span>Upload File</span>
              <span>Map Fields</span>
              <span>Preview</span>
              <span>Complete</span>
            </div>
            <p className="sm:hidden mt-2 text-center text-xs text-gray-500">Step {currentStep} of 4</p>
          </div>

          {/* Step Content */}
          <div className="mb-8">
            {renderStepContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

// Edit Item Modal Component
const EditItemModal = ({ item, onSave, onCancel }) => {
  const [editedItem, setEditedItem] = useState({ ...item });

  const handleFieldChange = (field, value) => {
    setEditedItem(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    onSave(editedItem);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-2xl w-full max-h-[92dvh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Edit Item</h3>
            <button
              onClick={onCancel}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 touch-manipulation" aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Item Name *</label>
              <input
                type="text"
                value={editedItem.itemName}
                onChange={(e) => handleFieldChange('itemName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={editedItem.description}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Rate</label>
              <input
                type="number"
                value={editedItem.defaultRate}
                onChange={(e) => handleFieldChange('defaultRate', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
              <input
                type="text"
                value={editedItem.quantityMeasurement}
                onChange={(e) => handleFieldChange('quantityMeasurement', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Barcode Number</label>
              <input
                type="text"
                value={editedItem.barcodeNumber}
                onChange={(e) => handleFieldChange('barcodeNumber', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">HSN Code</label>
              <input
                type="text"
                value={editedItem.hsnCode}
                onChange={(e) => handleFieldChange('hsnCode', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">GST Rate (%)</label>
              <input
                type="number"
                value={editedItem.gstRate}
                onChange={(e) => handleFieldChange('gstRate', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <input
                type="text"
                value={editedItem.category}
                onChange={(e) => handleFieldChange('category', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Opening Stock</label>
              <input
                type="number"
                value={editedItem.openingStock}
                onChange={(e) => handleFieldChange('openingStock', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Item Type</label>
              <AppSelect
                value={editedItem.itemType}
                onChange={(e) => handleFieldChange('itemType', e.target.value)}
                aria-label="Item type"
                sheetTitle="Item type"
                options={[
                  { value: 'product', label: 'Product' },
                  { value: 'service', label: 'Service' },
                  { value: 'restaurant', label: 'Restaurant' },
                  { value: 'other', label: 'Other' },
                ]}
                triggerClassName={`${APP_SELECT_FILTER_TRIGGER}`}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleImport;
